import {BaseFactory} from "../../../infrastructure/factories/base-factory";
import {AthleteGameLineupsResponseApiModel} from "../athlete-game-lineups-response.api-model";
import {AthleteGameLineupsDataSource} from "../athlete-game-lineups.data-source";
import {SportDomainModel} from "../../sports/models/sport-domain-model";
import {CompetitionDomainModel} from "../../common/competition/models/competition-domain-model";
import {CompetitorDomainModel} from "../../common/competitor/models/competitor-domain-model";
import {CompetitionApiModel} from "../../common/competition/models/competition-api-model";
import {CountryDomainModel} from "../../common/country/models/country-domain-model";
import {CountryFactory} from "../../common/country/domain-services/country-factory";
import {CompetitorFactory} from "../../common/competitor/domain-services/competitor-factory";
import {CompetitionFactory} from "../../common/competition/domain-services/competition-factory";
import {DatetimeUtils} from "../../../infrastructure/utils/datetime-utils";
import {PartialGameFactory} from "../../partial-game/services/partial-game.factory";
import {LoggerUtils} from "../../../infrastructure/utils/logger/logger-utils";
import {TermsUtils} from "../../../infrastructure/utils/terms-utils";
import {CountryApiModel} from "../../common/country/models/country-api-model";
import {LineupsMemberDomainModel} from "../../yard/lineups/lineups-member/lineups-member-domain-model";
import type {LineupsMemberApiModel} from "../../yard/lineups/lineups-member/lineups-member-api-model";
import type {StatsCategoryApiModel} from "../../yard/lineups/statsCategory/stats-category.api-model";
import {MemberDomainModel} from "../../yard/member/member-domain-model";
import {PartialGameDomainModel} from "../../partial-game/models/partial-game-domain-model";
import {CompetitorApiModel} from "../../common/competitor/models/competitor-api-model";
import {LineupFactory} from "../../yard/lineups/lineup.factory";
import {ChartEventsDomainModel} from "../../full-game/models/chart-events/chart-events.domain-model";
import {StatsCategoryDomainModel} from "../../yard/lineups/statsCategory/stats-category.domain-model";
import {MemberFactory} from "../../yard/member/member-factory";

export class AthleteGameLineupsFactory extends BaseFactory {
  countryFactory: CountryFactory;
  competitorFactory: CompetitorFactory;
  competitionFactory: CompetitionFactory;
  lineupFactory: LineupFactory;
  memberFactory: MemberFactory;

  static injectables = [...BaseFactory.injectables, CompetitionFactory, CompetitorFactory, CountryFactory, DatetimeUtils, PartialGameFactory, LineupFactory, MemberFactory];

  constructor(loggerUtils: LoggerUtils, termsUtils: TermsUtils, competitionFactory: CompetitionFactory, competitorFactory: CompetitorFactory, countryFactory: CountryFactory, datetimeUtils: DatetimeUtils, partialGameFactory: PartialGameFactory, lineupFactory: LineupFactory, memberFactory: MemberFactory) {
    super(loggerUtils, termsUtils);

    this.competitorFactory = competitorFactory;
    this.competitionFactory = competitionFactory;
    this.countryFactory = countryFactory;
    this.datetimeUtils = datetimeUtils;
    this.partialGameFactory = partialGameFactory;
    this.lineupFactory = lineupFactory;
    this.memberFactory = memberFactory;
  }

  createResponse(apiModel: AthleteGameLineupsResponseApiModel) {
    const responseApiModel: AthleteGameLineupsResponseApiModel = new AthleteGameLineupsResponseApiModel(apiModel);

    return responseApiModel;
  }

  createDomainModel(response: AthleteGameLineupsResponseApiModel): AthleteGameLineupsDataSource {
    const countries: Map<Number, CountryDomainModel> = new Map();
    response.countries?.forEach((country: CountryApiModel) => {
      countries.set(country.id, new CountryDomainModel(country));
    });
    const sport: SportDomainModel = response.sports.first();
    const sports: Map<Number, SportDomainModel> = this.createDomainMap(response.sports, SportDomainModel, 'id');
    const competitions: Map<Number, CompetitionDomainModel> = this.competitionFactory.createDomainMap(response.competitions, CompetitionDomainModel, 'id', response.sports, response.countries);
    const gamesMap: Map<Number, PartialGameDomainModel> = this.partialGameFactory.createDomainMap(response.games, PartialGameDomainModel, 'id', competitions, sports, countries);

    const competitionsList: Array<CompetitionDomainModel> = response.competitions?.map((competition: CompetitionApiModel) => {
      const country: CountryDomainModel = countries.get(competition.countryId);

      return new CompetitionDomainModel(competition, country, sport);
    })

    const countriesList: Array<CountryDomainModel> = response.countries?.map((country: CountryApiModel) => new CountryDomainModel(country));
    const competitorsList: Array<CompetitorDomainModel> = response.competitors?.map((competitor: CompetitorApiModel) => this.competitorFactory.createDomainModel(competitor, countries.get(competitor.countryId), sports.get(competitor.sportId)));
    const isNational = competitionsList?.first()?.isNationalTeams;
    const members: Array<LineupsMemberDomainModel> = response.members?.map((member: LineupsMemberApiModel) => {
      const athlete = new MemberDomainModel(member, isNational, sport);
      return this.lineupFactory.createLineupsMemberDomainObject(athlete, member, isNational, sport);
    });

    const statsCategories: Array<StatsCategoryDomainModel> = response.statsCategories?.map((statsCategory: StatsCategoryApiModel) => new StatsCategoryDomainModel(statsCategory));
    const chartEvents: ChartEventsDomainModel = response.chartEvents ? new ChartEventsDomainModel(response.chartEvents, this.memberFactory.createMap(members)) : null;

    const athleteGameLineupsDataSource: AthleteGameLineupsDataSource = new AthleteGameLineupsDataSource(response, countriesList, competitionsList, competitorsList, gamesMap, members, statsCategories, chartEvents);

    return athleteGameLineupsDataSource;
  }
}
